import React, { useState, useCallback, useEffect, useRef } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  Marker,
  // DirectionsService,
  // DirectionsRenderer,
  Circle,
  InfoWindow,
  TrafficLayer,
  Polyline,
} from "@react-google-maps/api";
import SVG from "react-inlinesvg";
import { WhatsappIcon, WhatsappShareButton } from "react-share";
import { IoIosCopy } from "react-icons/io";
import copy from "clipboard-copy";
import { post } from "../components/api";
import ModalPopup from "../components/ModalPopup";
import { SweetAlert } from "../helpers/helper";
import { MdOutlineDirections, MdOutlineWatchLater } from "react-icons/md";
import VehicleMarker from "./customMarker";
import View from "../assets/emergency_services/view.svg";
import FileShare from "../assets/emergency_services/file-share.svg";
import Zoom from "../assets/emergency_services/zoom.svg";
import TrafficSignal from "../assets/emergency_services/traffic.svg";
import stopIcon from "../assets/vehicles/stoppedHistory.svg";
import fuelPin from "../assets/emergency_services/fuel_station_pin.png";
import { useNavigate, useParams } from "react-router-dom";
const libraries = ["places", "geometry", "drawing"];

const mapContainerStyle = {
  width: "100%",
  height: "525px",
};
const LiveMapContainer = ({
  socketData,
  currentVehiclePosition,
  locationData,
  nearbyPlaces,
  fuelMarker,
  emergencyServiceType,
  vehicleId,
  currentVehicleData,
  timeFilter,
  stopsData,
  routeType,
  defaultZoom,
  networkStrength,
  changeRouteType,
  toggleDrawer,
  selectEmergencyService,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const [openInfoWindow, setOpenInfoWindow] = useState(false);
  const [stopsInfoWindow, setOpenInfoStopWindow] = useState("");
  const [nearByInfoWindow, setNearByInfoWindow] = useState("");
  const [mapType, setMapType] = useState("roadmap");
  const [zoomLevel, setZoomLevel] = useState(false);
  const [showTraffic, setShowTraffic] = useState(false);
  const [showModalPopup, setShowModalPopup] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState("");
  const [selectedDay, setSelectedDay] = useState("0d");
  const [selectedHour, setSelectedHour] = useState("0h");
  const [errorMessage, setErrorMessage] = useState("");
  const [currentPosition, setCurrentPosition] = useState(null);
  const [progress, setProgress] = useState([]);
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries,
  });
  const [mapBounds, setMapBounds] = useState(null);
  const mapRef = useRef(null);

  const onLoad = (map) => {
    mapRef.current = map;
  };

  useEffect(() => {
    if (
      routeType === "route-history" &&
      mapRef.current &&
      stopsData.length > 1
    ) {
      const bounds = new window.google.maps.LatLngBounds();
      stopsData.forEach((stop) => {
        if (stop.coordinates) {
          bounds.extend(
            new window.google.maps.LatLng(
              stop.coordinates[0],
              stop.coordinates[1]
            )
          );
        }
      });
      setMapBounds(bounds); // Set the bounds in state
      mapRef.current.fitBounds(bounds); // Set bounds to map
    }
  }, [routeType, stopsData]);

  useEffect(() => {
    if (routeType === "live") {
      setMapBounds(null);
    }
    if (currentVehiclePosition) {
      if (routeType === "live" && locationData.length <= 0) {
        setCurrentPosition({
          lat: currentVehiclePosition[0],
          lng: currentVehiclePosition[1],
          speed: 0,
        });
      } else {
        setCurrentPosition(currentVehiclePosition);
      }
    }
  }, [routeType, currentVehiclePosition]);

  const moveObject = useCallback(async () => {
    const batchSize = 1;
    if (socketData.length > 0) {
      if (progress.length === 0) {
        setProgress([socketData[0]]);
        setCurrentPosition(socketData[0]);
      } else {
        const nextIndex = progress.length;
        const batch = socketData.slice(nextIndex, nextIndex + batchSize);
        if (batch.length > 0) {
          await interpolateMovement(currentPosition, batch); // Pass the batch to interpolateMovement
          setProgress((prevProgress) => [...prevProgress, ...batch]);
          setCurrentPosition(batch[batch.length - 1]); // Set to the last point of the batch
        }
      }
    }
  }, [progress, socketData, currentPosition]);

  useEffect(() => {
    const interval = setInterval(() => {
      moveObject();
    }, 1000);
    return () => clearInterval(interval);
  }, [moveObject]);

  const interpolateMovement = async (startPos, positions) => {
    const totalDuration = 1000; // Total duration for animating the entire batch
    const stepsPerTransition = 60; // Number of steps per transition between two points
    const stepDuration =
      totalDuration / (positions.length * stepsPerTransition); // Calculate step duration for the entire batch
    for (let i = 0; i < positions.length; i++) {
      const endPos = positions[i];
      const latDiff = (endPos.lat - startPos.lat) / stepsPerTransition;
      const lngDiff = (endPos.lng - startPos.lng) / stepsPerTransition;
      for (let step = 0; step < stepsPerTransition; step++) {
        const newLat = startPos.lat + latDiff * step;
        const newLng = startPos.lng + lngDiff * step;
        setCurrentPosition({
          lat: newLat,
          lng: newLng,
          angle: startPos.angle,
          speed: startPos.speed,
          address: startPos.address,
        });
        await sleep(stepDuration);
      }
      // After interpolating to the current position, update startPos for the next iteration
      startPos = endPos;
    }

    // Ensure the final position is set to the last point in the batch
    setCurrentPosition(positions[positions.length - 1]);
  };

  const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  // copy link
  const copyToClipboard = () => {
    copy(shareUrl);
    SweetAlert("Link copy successfully.", false);
  };

  const toggleMapType = (type) => {
    if (type == "view-type") {
      const newMapType =
        mapRef?.current?.mapTypeId === "roadmap" ? "satellite" : "roadmap";
      const tempShowTrafic = newMapType === "satellite" ? false : showTraffic;

      const currentCenter = mapRef.current.getCenter();
      const currentZoom = mapRef.current.getZoom();

      // Change the map type
      mapRef.current.setMapTypeId(newMapType);

      // Restore center and zoom
      mapRef.current.setCenter(currentCenter);
      mapRef.current.setZoom(currentZoom);
      setShowTraffic(tempShowTrafic);
      setMapType(newMapType); // Update mapType state
    }
    if (type == "traffic-show") {
      setShowTraffic(!showTraffic);
    }
  };

  const NetworkSvg = ({ value }) => {
    const bars = [
      { x: 0, y: 25, width: 4, height: 10 },
      { x: 7, y: 18, width: 4, height: 17 },
      { x: 14, y: 13, width: 4, height: 22 },
      { x: 21, y: 7, width: 4, height: 28 },
      { x: 28, y: 0, width: 4, height: 35 },
    ];
    return (
      <svg
        width="20"
        height="24"
        viewBox="0 0 32 35"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        {bars.map((bar, index) => (
          <rect
            key={index}
            x={bar.x}
            y={bar.y}
            width={bar.width}
            height={bar.height}
            fill={index < value ? "#4CAF50" : "#DBDBDB"}
          />
        ))}
      </svg>
    );
  };

  // close modal
  const handleCloseModal = () => {
    setShowModalPopup(false);
  };

  const handleShowModal = async () => {
    setShowModalPopup(true);
  };

  const handleMarkerClick = () => {
    setOpenInfoWindow(!openInfoWindow);
    setOpenInfoStopWindow("");
  };

  // stop marker click
  const handleStopsMarkerClick = (index) => {
    setOpenInfoStopWindow(index);
    setOpenInfoWindow(false);
  };

  // Nearby marker click
  const handleNearByMarkerClick = (index) => {
    setNearByInfoWindow(index);
    setOpenInfoWindow(false);
  };

  const mapOptions = {
    mapTypeId: "roadmap",
    disableDefaultUI: true,
    zoomControl: false,
    scaleControl: false,
    streetViewControl: false,
    fullscreenControl: false,
    mapTypeControl: false,
    rotateControl: false,
    trafficControlOptions: {
      mapTypeIds: ["roadmap", "satellite"],
      // style: window.google.maps.TrafficStyle,
    },
  };

  const handleCreateShareLink = async () => {
    if (selectedDay === "0d" && selectedHour === "0h") {
      setErrorMessage("Please select at least 1 hour to create the link.");
    } else {
      try {
        setLoading(true);
        let jsonData = {
          vehicle_id: vehicleId,
        };
        if (selectedDay !== "0d") {
          jsonData.timeframe = selectedDay;
        }
        if (selectedHour !== "0h") {
          jsonData.timeframe = selectedHour;
        }
        const getToken = await post("/generate-token-web", jsonData);
        if (getToken.data && getToken.data.token !== undefined) {
          setLoading(false);
          setShareUrl(process.env.REACT_APP_SITE_URL + getToken.data.token);
          SweetAlert(getToken.message, false);
        } else {
          setShareUrl("");
          SweetAlert("The link has expired. Please request a new one.", true);
          setLoading(false);
          setShowModalPopup(false);
        }
      } catch (error) {
        SweetAlert(error.message, true);
        setLoading(false);
        setShareUrl("");
        setShowModalPopup(false);
      }
    }
  };

  const handleDirection = () => {
    const location = locationData[locationData.length - 1];
    const { lat, lng } = location;
    const url = `https://www.google.com/maps?q=${lat},${lng}&z=15`;
    window.open(url, "_blank");
  };

  if (loadError) {
    return <div>Error loading maps</div>;
  }

  if (!isLoaded) {
    return <div>Loading maps</div>;
  }
  return (
    <div className=" main-map-div">
      <div className="google-map-parent">
        {isLoaded && (
          <div className="google-map-parent">
            <GoogleMap
              onLoad={onLoad}
              mapContainerStyle={mapContainerStyle}
              zoom={zoomLevel ? 14 : defaultZoom}
              center={
                fuelMarker.length !== 0
                  ? {
                      lat: fuelMarker.coordinates[0],
                      lng: fuelMarker.coordinates[1],
                    }
                  : mapBounds
                  ? {
                      lat: mapBounds.getCenter().lat(),
                      lng: mapBounds.getCenter().lng(),
                    }
                  : {
                      lat:
                        timeFilter === "today"
                          ? currentPosition?.lat
                          : locationData[locationData.length - 1].lat,
                      lng:
                        timeFilter === "today"
                          ? currentPosition?.lng
                          : locationData[locationData.length - 1].lng,
                    }
              }
              options={mapOptions}
            >
              {currentPosition && (
                <VehicleMarker
                  currentVehicleData={currentVehicleData}
                  currentPosition={currentPosition}
                  locationData={locationData}
                  timeFilter={timeFilter}
                  handleMarkerClick={handleMarkerClick}
                  openInfoWindow={openInfoWindow}
                />
              )}
              {routeType == "route-history" &&
                stopsData.length > 0 &&
                stopsData.map((stop, index) => (
                  <Marker
                    key={index}
                    position={{
                      lat: stop.coordinates[0],
                      lng: stop.coordinates[1],
                    }}
                    title={stopsData.length - index}
                    label={{
                      text: `${stopsData.length - index}`,
                      color: "white",
                      fontSize: "12px",
                    }}
                    icon={{
                      url: stopIcon,
                      scaledSize: { width: 30, height: 30 },
                      scale: 7,
                    }}
                    onClick={() => handleStopsMarkerClick(index)}
                  >
                    {stopsInfoWindow !== "" && stopsInfoWindow == index && (
                      <InfoWindow
                        onCloseClick={() => handleStopsMarkerClick("")}
                      >
                        <div
                          style={{
                            color: "#fff",
                            width: "280px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                          }}
                          className="stops"
                        >
                          <p className="mb-2">
                            <MdOutlineWatchLater size={18} /> {stop.time}
                          </p>
                          <p className="mb-2">
                            {stop.startingTime} - {stop.endingTime}
                          </p>
                          <p className="mb-2 text-left">{stop.address}</p>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              {/* Render circle for geofence (10 km radius) around current location */}
              {emergencyServiceType !== "" && (
                <Circle
                  center={{
                    lat: parseFloat(locationData[locationData.length - 1]?.lat),
                    lng: parseFloat(locationData[locationData.length - 1]?.lng),
                  }}
                  radius={10000} // Radius in meters (10 km)
                  options={{
                    strokeColor: "#2A7C76",
                    strokeOpacity: 0.8,
                    strokeWeight: 2,
                    fillColor: "#2A7C76",
                    fillOpacity: 0.2,
                  }}
                />
              )}
              {fuelMarker?.length !== 0 && (
                <Marker
                  position={{
                    lat: parseFloat(fuelMarker.coordinates[0]),
                    lng: parseFloat(fuelMarker.coordinates[1]),
                  }}
                  icon={{
                    url: fuelPin,
                    scaledSize: { width: 25, height: 30 },
                    scale: 5,
                  }}
                  onClick={() => handleNearByMarkerClick(fuelMarker.address)}
                >
                  {nearByInfoWindow !== "" && (
                    <InfoWindow
                      onCloseClick={() => handleNearByMarkerClick("")}
                    >
                      <div
                        style={{
                          color: "#fff",
                          width: "200px",
                          display: "flex",
                          padding: "1px",
                          textAlign: "left",
                        }}
                        className="live"
                      >
                        <p>{fuelMarker.address}</p>
                      </div>
                    </InfoWindow>
                  )}
                </Marker>
              )}
              {emergencyServiceType !== "" &&
                nearbyPlaces.map((place, index) => (
                  <Marker
                    key={index}
                    position={{
                      lat: parseFloat(place.coordinates[0]),
                      lng: parseFloat(place.coordinates[1]),
                    }}
                    icon={{
                      url: place.pin,
                      scaledSize: { width: 25, height: 30 },
                      scale: 5,
                    }}
                    onClick={() => handleNearByMarkerClick(index)}
                  >
                    {nearByInfoWindow !== "" && nearByInfoWindow == index && (
                      <InfoWindow
                        onCloseClick={() => handleNearByMarkerClick("")}
                      >
                        <div
                          style={{
                            color: "#fff",
                            width: "200px",
                            display: "flex",
                            padding: "1px",
                            textAlign: "left",
                          }}
                          className="live"
                        >
                          <p>{place.address}</p>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
              <Polyline
                path={
                  timeFilter == "today"
                    ? [...locationData, ...progress]
                    : locationData
                }
                options={{ strokeColor: "#2A7C76", strokeWeight: 5 }}
              />
              {showTraffic && <TrafficLayer />}
            </GoogleMap>
          </div>
        )}
      </div>
      <div className="map-custom-icons">
        {networkStrength !== "" && (
          <div className="image-container mt-3 mb-2">
            <NetworkSvg value={networkStrength} />
          </div>
        )}
        <div
          className={
            mapType == "roadmap"
              ? `image-container mt-2 mb-2`
              : `image-container-active mt-2 mb-2`
          }
          onClick={() => toggleMapType("view-type")}
        >
          <SVG src={View} width={"20px"} />
        </div>
        <div
          className={
            showTraffic ? "image-container-active mb-2" : "image-container mb-2"
          }
          onClick={() => toggleMapType("traffic-show")}
        >
          <SVG src={TrafficSignal} width={"20px"} />
        </div>
        <div
          className={
            zoomLevel ? "image-container-active mb-2" : "image-container mb-2"
          }
          onClick={() => setZoomLevel(!zoomLevel)}
        >
          <SVG src={Zoom} width={"20px"} />
        </div>
        <div className="image-container mb-2" onClick={handleShowModal}>
          <SVG src={FileShare} width={"20px"} />
        </div>
        <div className="image-container" onClick={handleDirection}>
          {/* <SVG src={FileShare} width={'20px'} /> */}
          <MdOutlineDirections size={"25px"} />
        </div>
      </div>
      {showModalPopup && (
        <ModalPopup
          className="modal"
          show={handleShowModal}
          onHide={handleCloseModal}
          title="Share URL"
        >
          <div>
            <h6>Expire the share link after</h6>
            <div className="form-group row">
              <label className="col-sm-1 col-form-label">Days</label>
              <div className="col-sm-3">
                <select
                  className="form-control"
                  id="expireDays"
                  value={selectedDay}
                  onChange={(e) => setSelectedDay(e.target.value)}
                >
                  {Array.from({ length: 4 }, (_, i) => (
                    <option key={i} value={`${i}d`}>
                      {i}
                    </option>
                  ))}
                </select>
              </div>
              {selectedDay === "0d" && (
                <>
                  <label className="col-sm-1 mr-2 col-form-label">Hours</label>
                  <div className="col-sm-3 ml-2">
                    <select
                      className="form-control"
                      id="expireHours"
                      value={selectedHour}
                      onChange={(e) => setSelectedHour(e.target.value)}
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={`${i}h`}>
                          {i}
                        </option>
                      ))}
                    </select>
                  </div>
                </>
              )}
            </div>
            {errorMessage && (
              <div className="text-danger" style={{ marginTop: "4px" }}>
                {errorMessage}
              </div>
            )}
            {shareUrl !== "" && (
              <div className="mt-4">
                <h6>Share link for live tracking </h6>
                <div className="d-flex justify-content-center align-items-center">
                  <input
                    type="text"
                    className="form-control"
                    value={shareUrl}
                    disabled
                  />
                  <IoIosCopy
                    size={32}
                    className="cursor-pointer ml-10"
                    onClick={copyToClipboard}
                  />
                  <WhatsappShareButton
                    url={shareUrl}
                    className="Demo__some-network__share-button ml-10"
                  >
                    <WhatsappIcon size={32} />
                  </WhatsappShareButton>
                </div>
              </div>
            )}
            <hr className="mt-3 mb-3" />
            <div className="d-flex justify-content-end mt-2">
              <button
                disabled={loading}
                className="btn btn-success"
                style={{ fontSize: "0.85rem", marginLeft: "-10px" }}
                onClick={handleCreateShareLink}
              >
                Create Share Link
              </button>
            </div>
          </div>
        </ModalPopup>
      )}
      <div className="nearBy-div row">
        <div className=" col-md-7 col-lg-7 col-xs-12">
          <div className="nearBy-Icon">
            <svg
              width="26"
              height="26"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("garages")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M14.3333 14.3333L6 6"
                stroke="#5b8295"
                strokeWidth="1.5"
              />
              <path
                d="M4.3335 8.50016L8.50016 4.3335L3.50016 1.8335L1.8335 3.50016L4.3335 8.50016Z"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path
                d="M29.2915 10.9581C30.9763 9.27318 31.4152 6.81393 30.6075 4.7258L28.2375 7.09579H24.9042V3.76246L27.2742 1.39246C25.186 0.584914 22.7268 1.0236 21.0418 2.70855C19.3563 4.39411 18.9178 6.85456 19.7267 8.94321L8.94321 19.7267C6.85458 18.9178 4.39411 19.3563 2.70855 21.0418C1.0236 22.7268 0.584914 25.186 1.39246 27.2742L3.76246 24.9042H7.09579V28.2375L4.7258 30.6075C6.81393 31.4152 9.27318 30.9763 10.9581 29.2915C12.6424 27.6072 13.0815 25.1492 12.2751 23.0615L23.0615 12.2751C25.1492 13.0815 27.6072 12.6424 29.2915 10.9581Z"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
              <path
                d="M15.6616 20.1668L25.0016 29.507C25.8813 30.3866 27.3076 30.3866 28.1873 29.507L29.5068 28.1875C30.3866 27.3076 30.3866 25.8815 29.5068 25.0018L20.1668 15.6616"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinejoin="round"
              />
            </svg>

            <svg
              width="2"
              height="35"
              viewBox="0 0 2 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 0V48" stroke="#5b8295" />
            </svg>

            <svg
              width="26"
              height="23"
              viewBox="0 0 32 29"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("police_station")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M1.11985 11.101C0.293667 15.367 4.00956 17.5 4.00956 17.5H27.9924C27.9924 17.5 31.7063 15.367 30.8801 11.101C30.3597 8.41443 24.593 4.88182 20.3944 2.63438C18.3589 1.5448 17.341 1 16 1C14.6588 1 13.6411 1.5448 11.6055 2.63438C7.40694 4.88182 1.64017 8.41443 1.11985 11.101Z"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4.21244 17.5C2.00214 19.4873 1.31583 24.3641 5.95834 25.5755C9.04417 26.3806 12.4643 27.2305 14.411 27.7097C15.1967 27.9032 15.5896 28 16 28C16.4104 28 16.8032 27.9032 17.5889 27.7097C19.5356 27.2305 22.9558 26.3806 26.0416 25.5755C30.684 24.3641 29.9978 19.4873 27.7875 17.5"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13.5849 8.09476L15.2439 7.19215C15.7147 6.93595 16.2853 6.93595 16.7562 7.19215L18.4152 8.09476C18.8503 8.3315 19.0947 8.815 18.9655 9.28972C18.6393 10.4881 17.8371 12.3937 16 13C14.163 12.3937 13.3608 10.4881 13.0345 9.28972C12.9053 8.815 13.1497 8.3315 13.5849 8.09476Z"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg
              width="2"
              height="35"
              viewBox="0 0 2 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 0V48" stroke="#5b8295" />
            </svg>

            <svg
              width="26"
              height="19"
              viewBox="0 0 32 25"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("hospital")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M14.5 20.5005H20.5M18.25 5.50024H19.6643C21.6299 5.50024 22.6129 5.50024 23.4461 5.97203C24.2794 6.44382 24.835 7.31485 25.9462 9.05695C26.7253 10.2784 27.5432 11.1651 28.6933 11.9539C29.8511 12.748 30.4018 13.1363 30.7073 13.7594C31 14.3561 31 15.0629 31 16.4764C31 18.3398 31 19.2716 30.4765 19.8637C30.454 19.8892 30.431 19.914 30.4073 19.938C29.8562 20.5005 28.9891 20.5005 27.2545 20.5005H26.5"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M5.49999 20.5003C3.37868 20.5003 2.31802 20.5003 1.65901 19.8413C1 19.1822 1 18.1216 1 16.0002V5.50007C1 3.37872 1 2.31804 1.65901 1.65902C2.31802 1 3.37868 1 5.49999 1H13.75C15.8713 1 16.9319 1 17.591 1.65902C18.25 2.31804 18.25 3.37872 18.25 5.50007V20.5003H11.5"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M30.9999 16H29.4999"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9.99975 6.99976V12.9998M12.9997 9.9998H6.99976"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M23.4997 23.4998C25.1566 23.4998 26.4997 22.1567 26.4997 20.4998C26.4997 18.8429 25.1566 17.4998 23.4997 17.4998C21.8429 17.4998 20.4998 18.8429 20.4998 20.4998C20.4998 22.1567 21.8429 23.4998 23.4997 23.4998Z"
                stroke="#5b8295"
                strokeWidth="1.5"
              />
              <path
                d="M8.49999 23.4998C10.1568 23.4998 11.5 22.1567 11.5 20.4998C11.5 18.8429 10.1568 17.4998 8.49999 17.4998C6.84314 17.4998 5.5 18.8429 5.5 20.4998C5.5 22.1567 6.84314 23.4998 8.49999 23.4998Z"
                stroke="#5b8295"
                strokeWidth="1.5"
              />
            </svg>

            <svg
              width="2"
              height="35"
              viewBox="0 0 2 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 0V48" stroke="#5b8295" />
            </svg>

            <svg
              width="26"
              height="26"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("dhaba")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M1 16.0001V24.3335M1 24.3335H4.33331C6.6903 24.3335 7.86881 24.3335 8.60104 25.0656C9.33327 25.798 9.33327 26.9765 9.33327 29.3335V31.0001M1 24.3335V31.0001"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M2.66663 7.66667L11.5278 2.80575C13.7222 1.60192 14.8195 1 15.9999 1C17.1802 1 18.2775 1.60192 20.472 2.80575L29.3331 7.66667"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M25.9997 6V12.6667M5.99988 6V12.6667"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M31 16.0001V24.3335M31 24.3335H27.6667C25.3097 24.3335 24.1312 24.3335 23.3989 25.0656C22.6667 25.798 22.6667 26.9765 22.6667 29.3335V31.0001M31 24.3335V31.0001"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M7.6665 19.3335H15.9998M15.9998 19.3335H24.333M15.9998 19.3335V31.0002M15.9998 31.0002H14.3331M15.9998 31.0002H17.6664"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg
              width="2"
              height="35"
              viewBox="0 0 2 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 0V48" stroke="#5b8295" />
            </svg>

            <svg
              width="26"
              height="26"
              viewBox="0 0 32 32"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("parking")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M16 31C24.2843 31 31 24.2843 31 16C31 7.71573 24.2843 1 16 1C7.71573 1 1 7.71573 1 16C1 24.2843 7.71573 31 16 31Z"
                stroke="#5b8295"
                strokeWidth="1.5"
              />
              <path
                d="M11.5 17.5V10.6C11.5 10.0416 11.5 9.76243 11.5734 9.53647C11.7218 9.07982 12.0798 8.72179 12.5365 8.57341C12.7624 8.5 13.0415 8.5 13.6 8.5H17.5C19.9854 8.5 22 10.5147 22 13C22 15.4854 19.9854 17.5 17.5 17.5H11.5ZM11.5 17.5V25"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>

            <svg
              width="2"
              height="35"
              viewBox="0 0 2 48"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path d="M1 0V48" stroke="#5b8295" />
            </svg>

            <svg
              width="26"
              height="23"
              viewBox="0 0 32 29"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              onClick={() => selectEmergencyService("fuel_station")}
              style={{ cursor: "pointer" }}
            >
              <path
                d="M13.6939 16L11.6029 18.7186C11.3788 19.0099 11.5394 19.4292 11.9048 19.5067L14.0952 19.9716C14.4849 20.0542 14.6334 20.5183 14.361 20.8016L11.7666 23.5"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 11.5H22"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M4 28V10C4 5.75736 4 3.63604 5.31802 2.31802C6.63604 1 8.75736 1 13 1C17.2426 1 19.3639 1 20.6819 2.31802C22 3.63604 22 5.75736 22 10V28H4Z"
                stroke="#5b8295"
                strokeWidth="1.5"
              />
              <path
                d="M1 28H25"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M22 17.5H24.5001C24.9651 17.5 25.1976 17.5 25.3882 17.5511C25.9059 17.6897 26.3103 18.0942 26.4489 18.6118C26.5 18.8025 26.5 19.0349 26.5 19.4999V21.25C26.5 22.4926 27.5074 23.5 28.75 23.5C29.9926 23.5 31 22.4926 31 21.25V11.8166C31 10.915 31 10.4641 30.8713 10.039C30.7426 9.61382 30.4924 9.23871 29.9923 8.48845L28.8321 6.74807C28.3123 5.96834 27.4371 5.5 26.5 5.5"
                stroke="#5b8295"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>
        <div className="col-md-5 col-lg-5 col-xs-12">
          <div className="Live-Button-Div">
            <button onClick={() => changeRouteType()}>Live</button>
            <button className="route-button" onClick={toggleDrawer}>
              Route History
            </button>
            <button
              onClick={() =>
                navigate(`/playback/${params?.id}`, {
                  state: { id: vehicleId },
                })
              }
            >
              PlayBack
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveMapContainer;
